/**
 * Clinic-session presentation (M14-T04, ADR-0030). The API returns structured
 * parts (`day_of_week` 0 = Monday, `part_of_day`, optional `start_time`/`end_time`);
 * the frontend formats. This is the ONE place that formatting and the weekday
 * convention live — the profile, the results card, and the T08 date picker all
 * use it, so the Python(0=Mon)/JS(0=Sun) mismatch is handled in exactly one spot.
 */
/** The display fields a session label needs — `id` is not required, so both a
 * full `ClinicSession` and a request's stored session shape can be formatted. */
export type SessionLike = {
  day_of_week: number
  part_of_day: string
  start_time?: string | null
  end_time?: string | null
}

/** 0 = Monday … 6 = Sunday (the backend / Python convention). */
export const DAY_NAMES_SHORT = [
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
  'Sun',
] as const

/** Backend weekday (0 = Monday) → JS `Date` day (0 = Sunday). */
export function backendWeekdayToJs(dayOfWeek: number): number {
  return (dayOfWeek + 1) % 7
}

/** JS `Date` day (0 = Sunday) → backend weekday (0 = Monday). */
export function jsDayToBackendWeekday(jsDay: number): number {
  return (jsDay + 6) % 7
}

/** Strip seconds from an 'HH:MM[:SS]' time and drop a leading zero for display. */
function shortTime(t: string): string {
  const [h, m] = t.split(':')
  const hour = String(Number(h))
  return m && m !== '00' ? `${hour}:${m}` : hour
}

/**
 * A human label for a session: "Tue morning", or "Tue morning · 9–12" when the
 * hospital gave times. `part_of_day` is authoritative; times are a refinement.
 */
export function sessionLabel(session: SessionLike): string {
  const day = DAY_NAMES_SHORT[session.day_of_week] ?? '?'
  const base = `${day} ${session.part_of_day}`
  if (session.start_time && session.end_time) {
    return `${base} · ${shortTime(session.start_time)}–${shortTime(session.end_time)}`
  }
  return base
}
